import React from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import { ChevronRight, MapPin, Calendar } from 'lucide-react';
import { DIALOG_TYPE } from '../../../../constants/DialogTypes';
import { Button } from '../../../../ui/components/Common/Buttons/Button';
import type { IOrganization } from '../../interfaces';
import { getInitials } from '../functions';
import type { OrganizationDetailTab } from '../interfaces';

interface IDetailsHeaderProps {
  organization: IOrganization;
  tabs: OrganizationDetailTab[];
  activeTab: OrganizationDetailTab;
  onTabChange: (tab: OrganizationDetailTab) => void;
}

export const DetailsHeader: React.FC<IDetailsHeaderProps> = ({
  organization,
  tabs,
  activeTab,
  onTabChange,
}) => {
  const history = useHistory();
  const initials = getInitials(organization.organizationName ?? '');

  return (
    <>
      <Breadcrumbs
        separator={<ChevronRight size={14} color="#94a3b8" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => history.push('/organizations')}
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { color: 'text.primary' },
          }}
        >
          Organizations
        </Link>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
          {organization.organizationName}
        </Typography>
      </Breadcrumbs>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 6,
          p: { xs: 3, md: 4 },
          mb: 4,
          boxShadow: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { lg: 'center' },
            gap: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={organization.companyLogo}
              alt={organization.organizationName || 'Company Logo'}
              sx={{
                width: 80,
                height: 80,
                borderRadius: 4,
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 700,
                border: '2px solid',
                borderColor: 'divider',
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em' }}>
                  {organization.organizationName}
                </Typography>
                <Chip
                  label="Enterprise"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.625rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRadius: 2,
                    height: 22,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700, mb: 1 }}>
                Tenant ID:{' '}
                <Typography component="span" variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  ACME-908877-ORG
                </Typography>
              </Typography>
              <Stack direction="row" spacing={3} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                  <MapPin size={14} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    San Francisco, CA
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                  <Calendar size={14} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Joined June 2021
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              label="Edit Profile"
              variant="primary"
              sx={{ borderRadius: 3, px: 3, py: 1.25, fontWeight: 700, boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.2)" }}
            />
            <Button
              label="Manage Licenses"
              variant="secondary"
              sx={{ borderRadius: 3, px: 3, py: 1.25, fontWeight: 700, borderColor: "#e2e8f0", "&:hover": { bgcolor: "#f8fafc" } }}
            />
            <Button
              label="Suspend"
              variant="primary"
              dialogType={DIALOG_TYPE.ERROR}
              sx={{ borderRadius: 3, px: 3, py: 1.25, fontWeight: 700, boxShadow: "0 10px 15px -3px rgba(239, 68, 68, 0.2)" }}
            />
          </Stack>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val: OrganizationDetailTab) => onTabChange(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'uppercase',
                fontWeight: 800,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                px: 2.5,
                py: 1.5,
                minHeight: 'auto',
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab} label={tab} value={tab} />
            ))}
          </Tabs>
        </Box>
      </Paper>
    </>
  );
};
